// SEO-12 · /resources/gst-on-school-fees-invoice
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • `backend/app/core/fee_gst.py`: each fee head may carry `hsn_sac` + `gst_rate` (0 = exempt);
//     stored amounts are GST-INCLUSIVE (what the parent pays) and the tax is BACK-COMPUTED, so
//     adding tax modelling never changes a payable total; intra-state billing splits evenly into
//     CGST + SGST with integer paise (cgst + sgst == tax exactly).
//   • Negative (waiver) lines are allocated proportionally across the positive lines BEFORE the
//     back-computation — audit X-12 — because tax follows the NET consideration; a positive-only
//     invoice is byte-identical to the pre-change output.
//   • Receipt template can show an opt-in CGST/SGST breakup — `core/receipt_template.py`.
//   • The module docstring's own framing ("tuition is GST-exempt, transport/hostel/canteen etc. are
//     taxable") is reproduced as the PRODUCT'S DEFAULT MODEL, with the school's own position
//     explicitly deferred to its tax adviser. This article gives no tax advice.
// NO tax advice, no rate table asserted as current law, no compliance claim.

const article = {
  slug: "gst-on-school-fees-invoice",
  title: "GST on school fees: what the invoice has to carry",
  seoTitle: "GST on School Fees in India: Invoicing | Vidyapeeth360",
  description:
    "Why tax on school fees belongs per fee head, why inclusive amounts are the safe default, and what a waiver does to the calculation.",
  topic: "Fees & finance",
  topicKey: "fees",
  readingMinutes: 5,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "Tax on school fees is not one decision for the school — it is a different decision per fee head. Software that models it as a single switch produces invoices that are wrong in a way nobody notices until an assessment.",
  moduleLinks: ["fee-collection", "receipt-generation", "fee-structure"],
  conversion: {
    to: "/contact",
    label: "Review your fee heads with us",
    note: "We will show how heads, rates and waivers compute — your own tax position stays a matter for your adviser.",
  },
  sections: [
    {
      id: "per-head",
      heading: "The unit of taxation is the head, not the invoice",
      blocks: [
        {
          t: "p",
          text: "A term invoice at an Indian school typically mixes services the GST law treats differently. The exemption for education services is set out in the government's own notification; ancillary services a school also sells — transport, hostel, canteen, uniforms — are not automatically inside it. One invoice, two treatments, sometimes more.",
        },
        {
          t: "p",
          text: "So the invoice cannot carry a single tax rate. Each head needs its own classification code and its own rate, including an explicit zero where the head is exempt. \"Exempt\" recorded as a rate is information; \"exempt\" recorded as a blank is an assumption you cannot audit later.",
        },
        {
          t: "note",
          title: "This is a modelling guide, not tax advice",
          text: "Which of your heads are taxable, at what rate, and whether your school is registered at all are questions for your school's own tax adviser and its assessing authority. Everything below is about representing the answer correctly once you have it — a good structure survives a change of position; a blended number does not.",
        },
      ],
    },
    {
      id: "inclusive",
      heading: "Inclusive amounts, back-computed tax",
      blocks: [
        {
          t: "p",
          text: "There are two ways to hold the numbers. Store the amount excluding tax and add it at the end, or store what the parent actually pays and derive the tax portion from inside it.",
        },
        {
          t: "p",
          text: "For a school, the second is almost always right. The fee notice, the circular and the parent's expectation are all denominated in the total. If the system stores an exclusive base and adds tax, the printed total drifts from the published fee by a rounding paisa or by a whole rate change — and the office ends up explaining arithmetic instead of collecting money.",
        },
        {
          t: "ul",
          items: [
            "**Store what the parent pays.** ₹4,500 for transport is ₹4,500 on the notice, on the invoice and in the bank.",
            "**Derive the taxable value and the tax from it.** The base is the total scaled down by the rate; the difference is the tax.",
            "**Split intra-state supply evenly into CGST and SGST**, and make the two halves add back to the tax exactly — in integer paise, with the odd paisa assigned deterministically rather than lost to rounding.",
            "**Never let the tax model change a payable total.** Turning tax representation on for an existing structure should leave every existing invoice's amount identical. If it does not, the model is being used to re-price, which is a different decision entirely.",
          ],
        },
      ],
    },
    {
      id: "waivers",
      heading: "What a waiver does to the calculation",
      blocks: [
        {
          t: "p",
          text: "This is the part most fee systems get wrong, and it is invisible until you check one invoice by hand.",
        },
        {
          t: "p",
          text: "A concession posted as a negative line reduces what the family actually pays. Tax follows the consideration actually received, so the waiver has to reduce the taxable base too — and because the invoice has several heads at different rates, it has to reduce them proportionally, not be dumped on whichever line is first or largest.",
        },
        {
          t: "p",
          text: "Concretely: an invoice with an exempt tuition line and a taxable transport line, discounted by ₹3,000, must apportion that ₹3,000 across both in proportion to their amounts before the tax portion of the transport line is back-computed. Apply the whole waiver to tuition and the invoice overstates the tax; apply it all to transport and it understates it. Both are arithmetic somebody may eventually re-perform.",
        },
      ],
    },
    {
      id: "the-receipt",
      heading: "What the parent should see",
      blocks: [
        {
          t: "p",
          text: "A receipt that shows only a total is fine for most families and useless for the ones who need the tax detail — a company reimbursing hostel charges, a family claiming an allowance, an employer's accounts desk.",
        },
        {
          t: "ol",
          items: [
            "**Show the heads.** A parent should be able to see what they paid for without ringing the office.",
            "**Show the waiver as a line**, not as a smaller number. A discount the family cannot see is a discount they will ask about anyway.",
            "**Make the tax breakup available**, even if it is off by default. Turn it on when the school's position calls for it, and keep the classification code alongside the rate.",
            "**Number every receipt from one running series**, mark reprints as duplicates, and keep the numbers gap-free — a tax invoice with an improvised number is the one an auditor circles.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Each fee head can carry its own classification code and rate, with zero meaning exempt. Amounts are stored as the parent-facing, tax-inclusive figure, and the taxable value and tax are back-computed for the record — so adding tax modelling to an existing fee structure never moves a payable total. Intra-state tax splits evenly into CGST and SGST in integer paise, with the halves reconciling to the total exactly.",
        },
        {
          t: "p",
          text: "Negative concession lines are apportioned across the positive lines in proportion to their amounts before the split is computed, so tax is derived from the net consideration rather than from the gross. An invoice with no waivers computes identically to one with no tax modelling at all — the feature adds information, not drift.",
        },
        {
          t: "p",
          text: "The breakup is available on the invoice view and as an opt-in section of the receipt template, alongside the school's own masthead and declaration text. Receipts are numbered from a single sequential per-school register, and any reprint carries a visible duplicate mark.",
        },
        {
          t: "p",
          text: "The pieces are ordinary: heads defined once in the [fee structure](/modules/fee-structure), amounts collected through [fee collection](/modules/fee-collection), and the evidence printed by [receipt generation](/modules/receipt-generation). What makes it hold together is that no step re-derives the total — the number the parent was told is the number every other figure is computed from.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
